import type { ReactNode } from "react";
import { Building2, type LucideIcon } from "lucide-react";
import type { TFunction } from "i18next";
import { ROUTES } from "@/shared/config";
import { canAccessPlatformReports } from "@/entities/report/model/access";
import { consoleOrigin, isConsoleEnabled } from "@/shared/config/consoleHost";

export interface NavSubItem {
  href: string;
  label: string;
  active: boolean;
  visible?: boolean;
}

export interface NavItem {
  key: string;
  href: string;
  icon: LucideIcon;
  label: string;
  active: boolean;
  visible: boolean;
  /** Contador ao lado do rótulo (ex.: mensagens por ler). */
  badge?: number;
  /** Mostra o submenu mesmo quando o item em si nao e a rota ativa (ex.: dentro de uma obra). */
  expandChildren?: boolean;
  subItems?: NavSubItem[];
  submenu?: ReactNode;
}

interface PlatformNavDeps {
  t: TFunction;
  isActive: (path: string | string[]) => boolean;
  isPlatformAdmin: boolean;
}

/** Centro de entregas e erros são da plataforma: com o console ligado só abrem lá. */
export function platformToolsInApp(isPlatformAdmin: boolean): boolean {
  return isPlatformAdmin && !isConsoleEnabled();
}

export function buildPlatformNavItem({ t, isActive, isPlatformAdmin }: PlatformNavDeps): NavItem {
  // Organizações e armazenamento dependem da mesma claim; relatórios ainda têm mock próprio.
  const canListTenants = isPlatformAdmin;
  const canAccessReports = canAccessPlatformReports(isPlatformAdmin);
  // Com o console ligado, a plataforma vive no host dele: o item vira um link para lá.
  if (isConsoleEnabled()) {
    return {
      key: "platform",
      href: `${consoleOrigin()}${canListTenants ? "/tenants" : "/reports"}`,
      icon: Building2,
      label: t("sidebar.console"),
      active: false,
      visible: canListTenants || canAccessReports,
    };
  }
  return {
    key: "platform",
    href: canListTenants ? ROUTES.platformTenants : ROUTES.platformReports,
    icon: Building2,
    label: t("sidebar.platform"),
    active: isActive("/platform"),
    visible: canListTenants || canAccessReports,
    subItems: [
      { href: ROUTES.platformTenants, label: t("sidebar.sub.platformTenants"), active: isActive(ROUTES.platformTenants), visible: canListTenants },
      { href: ROUTES.platformReports, label: t("sidebar.sub.platformReports"), active: isActive(ROUTES.platformReports), visible: canAccessReports },
      { href: ROUTES.platformStorage, label: t("sidebar.sub.platformStorage"), active: isActive(ROUTES.platformStorage), visible: canListTenants },
    ],
  };
}

/** Abas do /me mostradas sob o perfil na sidebar. */
export function buildProfileTabs(t: TFunction, tab: unknown): NavSubItem[] {
  return [
    { href: `${ROUTES.me}?tab=details`, label: t("me.tabs.details"), active: tab === "details" || !tab },
    { href: `${ROUTES.me}?tab=wallet`, label: t("me.tabs.wallet"), active: tab === "wallet" },
    { href: `${ROUTES.me}?tab=account`, label: t("me.tabs.account"), active: tab === "account" },
  ];
}
